import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionButton,
  FileTrigger,
  Flex,
  ProgressCircle,
  Text,
  TextField,
  ToastQueue,
  View,
  Content,
} from "@adobe/react-spectrum";
import {
  normalizeAtollonNumericId,
  truncateText,
} from "../../../utils/helperUtil";
import {
  formStateHasNotifyUsers,
  getComposeSendPreflightFailure,
} from "../../../utils/composeSendPreflight";
import { useTranslation } from "react-i18next";
import {
  AttachmentIcon,
  CancelIcon,
  NewMessageIcon,
  TemplateIcon,
} from "../../../components/protected/Icons/IconsLib";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { WindowActionsBar } from "@progress/kendo-react-dialogs";
import { RadioButton } from "@progress/kendo-react-inputs";
import { Loader } from "@progress/kendo-react-indicators";
import { useDependency } from "../../../contexts/DependencyProvider";
import ContactsContactMultipleTag from "../../../components/protected/layout-components/FilterComponent/ContactsContactMultipleTagComponent/ContactsContactMultipleTag";
import ContactsFilterMultipleTag from "../../../components/protected/layout-components/FilterComponent/ContactsFilterMultipleTagComponent/ContactsFilterMultipleTag";
import WindowComponent from "../../../components/ui/WindowComponent";
import {
  openPdfContentInNewWindow,
  invokeNewMessageCreated,
} from "../../../utils/NavigationUtils";
import { useViewInfo } from "../../../hooks/UseViewInfo";
import TinyMCEEditor from "../../../components/ui/TinyMCEEditor/TinyMCEEditior";
import { useLocation } from "react-router-dom";
import { SessionInfo } from "../../../data/SessionInfo";
import {
  addKey,
  useKeyboardShortcuts,
} from "../../../hooks/UseKeyboardShortcuts";
import { usePreloadAssets } from "../../../hooks/UsePreloadAssets";
import { ContextSelection } from "../../../components/protected/Search/ContextSelection";
import type { ContextObject } from "../../../components/protected/Search/ContextSelection/types";
import styles from "./new_message.module.css";

// Centralized shared hooks
import {
  useNewMessageForm,
  useNewMessageSettings,
  useNewMessageFiles,
  sendMessage,
  saveDraftMessage,
  MessageOperationsConfig,
} from "../../../hooks/message";

export interface INewMessageDialogProps {
  windowIndex: number;
  selectedItemId: any;
  attachments?: any;
  notifyUsers?: any;
  objMessage?: any;
  replyType?: string;
  emailAttachments: any;
  close: () => void;
  forcedMessageType?: string;
  /** Whether the context is a folder (not a valid project/activity/contact context) */
  isFolderContext?: boolean;
}

function NewMessageDialog(props: Readonly<INewMessageDialogProps>) {
  const { state } = useLocation();
  const session: SessionInfo = state.session;
  const { isMobile } = useViewInfo();
  const {
    windowIndex,
    selectedItemId,
    close,
    attachments,
    notifyUsers,
    replyType,
    objMessage,
    forcedMessageType,
    isFolderContext,
  } = props;
  const { store, messageService, mailService } = useDependency();
  const { t } = useTranslation();

  // Local state and refs
  const txtSubjectRef = useRef<any>(null);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showCcNotify, setShowCcNotify] = useState<boolean>(false);
  const [fromEmailAddress, setFromEmailAddress] = useState<any[]>([]);
  const [messageTypeOptions, setMessageTypeOptions] = useState<any[]>([]);
  const [isProcessingAttachment, setIsProcessingAttachment] =
    useState<boolean>(false);
  const [processingAttachmentId, setProcessingAttachmentId] = useState<
    string | null
  >(null);

  // Use shared hooks
  const {
    formData: newEmailFields,
    formStateRef,
    setFormData: setNewEmailFields,
    createParams,
  } = useNewMessageForm();

  // Use shared settings hook
  const {
    isLoading: settingsLoading,
    mailboxSignature,
    changeMailbox,
    populateReplyTemplate,
  } = useNewMessageSettings({
    session,
    messageObj: objMessage,
    replyType,
    onSettingsLoaded: (settings, defaultFields) => {
      const forcedMessageTypeOption = forcedMessageType
        ? settings.messageTypes.find(
            (item: any) => String(item.type) === String(forcedMessageType),
          )
        : null;
      setFromEmailAddress(settings.mailboxes);
      setMessageTypeOptions(settings.messageTypes);
      setNewEmailFields({
        ...defaultFields,
        notify: notifyUsers || [],
        from: defaultFields.from,
        fromValue: defaultFields.fromValue,
        messageType: forcedMessageTypeOption?.type || defaultFields.messageType,
        messageTypeValue:
          forcedMessageTypeOption || defaultFields.messageTypeValue,
        mailBox: settings.mailboxSignature,
        emailContent: settings.mailboxSignature,
      });
    },
    onError: (error) => {
      console.error("Failed to load communication settings", { error });
    },
  });

  const {
    folderStatusMapping,
    projectStatusMapping,
    activityStatusMapping,
  } = usePreloadAssets();

  const [messageContextSelection, setMessageContextSelection] =
    useState<ContextObject | null>(null);


  const contextSelectionDefaultValue = useMemo(() => {
    if (isFolderContext) return null;
    const n = normalizeAtollonNumericId(selectedItemId);
    const defaultMessageNodeId = normalizeAtollonNumericId(
      session?.sessionInfo?.msgNode,
    );
    if (n == null) return null;
    if (defaultMessageNodeId != null && n === defaultMessageNodeId) return null;
    return n;
  }, [selectedItemId, session?.sessionInfo?.msgNode, isFolderContext]);

  useEffect(() => {
    setMessageContextSelection(null);
  }, [selectedItemId]);

  const onContextPathResolveError = useCallback((error: unknown) => {
    console.error("ContextSelection default context resolve failed", error);
  }, []);

  const resolvedContextId =
    messageContextSelection?.contextId ?? contextSelectionDefaultValue ?? "";

  const resolvedIsFolderContext =
    messageContextSelection === null
      ? contextSelectionDefaultValue != null
        ? false
        : Boolean(isFolderContext)
      : false;

  const displayContextSelection = useMemo(() => {
    if (!messageContextSelection) return null;

    return {
      ...messageContextSelection,
      pathSegmentContexts: messageContextSelection.pathSegmentContexts?.map(
        (segment) => ({
          id: segment.id,
          type: segment.type,
        }),
      ),
    };
  }, [messageContextSelection]);

  // Use shared file management hook
  const {
    fileList,
    handleFilesAsync,
    deleteFile,
    uploadFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    setFileList,
  } = useNewMessageFiles({
    contextId: resolvedContextId,
    fallbackTreeHandle: resolvedIsFolderContext
      ? objMessage?.treeHandle || session?.sessionInfo?.msgNode
      : undefined,
    initialAttachments: props.emailAttachments,
  });

  // Show loader while settings are loading
  useEffect(() => {
    setShowLoader(settingsLoading);
  }, [settingsLoading]);

  useEffect(() => {
    if ((newEmailFields.notify?.length ?? 0) > 0 || (notifyUsers?.length ?? 0) > 0) {
      setShowCcNotify(true);
    }
  }, [newEmailFields.notify, notifyUsers]);

  // Initialize file list from attachments
  useEffect(() => {
    if (attachments?.length > 0) {
      setFileList(attachments);
    }
  }, [attachments, setFileList]);

  // Handle reply template population
  useEffect(() => {
    if (replyType && objMessage) {
      const replyFields = populateReplyTemplate();
      if (replyFields) {
        setNewEmailFields((prev: any) => ({ ...prev, ...replyFields }));
        if (
          (replyFields.cc?.length ?? 0) > 0 ||
          (replyFields.notify?.length ?? 0) > 0
        ) {
          setShowCcNotify(true);
        }
      }
    }
  }, [
    mailboxSignature,
    replyType,
    objMessage,
    populateReplyTemplate,
    setNewEmailFields,
  ]);

  // Message operations config
  const getMessageOpsConfig = useCallback(
    (): MessageOperationsConfig => ({
      mailService,
      messageService,
      serverService: store.Server,
      sessionId: store.SessionId,
      contextId: resolvedContextId,
      messageTypes: messageTypeOptions,
      parentMessageId: objMessage?.id,
      // For replies/folder messages: use messageGroup from the original message
      messageGroupId: objMessage?.messageGroup,
      // Tree handle from the original message (for folder context)
      treeHandle: objMessage?.treeHandle,
      // User's default message group (fallback for folder contexts)
      userMessageGroup: session?.sessionInfo?.msgGroup,
      // User's default message tree handle (fallback for personal messages)
      userMessageTreeHandle: session?.sessionInfo?.msgNode,
      // Whether this is a folder context (not a valid project/activity/contact context)
      isFolderContext: resolvedIsFolderContext,
    }),
    [
      mailService,
      messageService,
      store.Server,
      store.SessionId,
      resolvedContextId,
      messageTypeOptions,
      objMessage,
      session?.sessionInfo?.msgGroup,
      session?.sessionInfo?.msgNode,
      resolvedIsFolderContext,
    ],
  );

  // Send email/message using shared utility
  const handleSendEmail = useCallback(async () => {
    const hasNotifyUsers = formStateHasNotifyUsers(formStateRef.current as any);
    const failure = getComposeSendPreflightFailure({
      hasNotifyUsers,
      resolvedIsFolderContext,
      messageType: (formStateRef.current as any)?.messageType,
    });
    if (failure === "notify_requires_project_or_activity") {
      ToastQueue.negative(
        t("please_choose_context", {
          ns: "layout_components",
          defaultValue:
            "Please choose a context (project or activity) when using Notify.",
        }),
        { timeout: 3000 },
      );
      return;
    }

    try {
      setShowLoader(true);
      const documentIds = await uploadFiles();
      const result = await sendMessage(
        getMessageOpsConfig(),
        formStateRef.current,
        documentIds,
        formStateRef.current.emailContent,
      );
      if (result.success) {
        invokeNewMessageCreated(resolvedContextId, "message_created");
        closeDialog();
      }
    } finally {
      setShowLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getMessageOpsConfig,
    uploadFiles,
    resolvedContextId,
    formStateRef,
    resolvedIsFolderContext,
    t,
  ]);

  // Save draft using shared utility
  const handleSaveDraft = useCallback(async () => {
    try {
      setShowLoader(true);
      const documentIds = await uploadFiles();
      const result = await saveDraftMessage(
        getMessageOpsConfig(),
        formStateRef.current,
        documentIds,
        formStateRef.current.emailContent,
      );
      if (result.success) {
        closeDialog();
      }
    } finally {
      setShowLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMessageOpsConfig, uploadFiles, formStateRef]);

  const handleClose = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      closeDialog();
    }
  };

  const closeDialog = () => {
    close();
  };

  const focusSubjectField = useCallback(() => {
    window.setTimeout(() => {
      const subjectRef = txtSubjectRef.current as any;
      if (subjectRef?.focus) {
        subjectRef.focus();
        return;
      }

      const subjectInput = document.querySelector(
        "#txtSubject input, #txtSubject",
      ) as HTMLInputElement | null;
      subjectInput?.focus();
      subjectInput?.select?.();
    }, 60);
  }, []);

  useKeyboardShortcuts(
    [
      ...addKey("Enter", { ctrl: true }, () => {
        if (!showLoader) {
          handleSendEmail();
        }
      }),
      ...addKey("z", { ctrl: true, shift: true }, () => {
        if (!showLoader) {
          closeDialog();
        }
      }),
      ...addKey("Escape", { ctrl: false, shift: false, alt: false }, () => {
        if (!showLoader) {
          closeDialog();
        }
      }),
    ],
    true,
  );

  useEffect(() => {
    if (!showLoader) {
      focusSubjectField();
    }
  }, [focusSubjectField, showLoader]);

  // Mailbox change handler using shared hook
  const setMailBox = async (event: any) => {
    const fromEmailId = fromEmailAddress.find(
      (item: any) => item.name === event.value.name,
    )?.id;
    const newSignature = await changeMailbox(fromEmailId);
    setNewEmailFields((prev: any) => ({
      ...prev,
      from: event.value.name,
      fromValue: { name: event.value.name, id: event.value.id },
      mailBox: newSignature,
      emailContent: prev.emailContent.replace(prev.mailBox || "", newSignature),
    }));
  };

  function renderDesktopEmailOptionalFields(): React.ReactElement {
    return (
      <>
        <Flex
          direction={"row"}
          top={"5px"}
          alignSelf={"stretch"}
          position={"relative"}
          flexShrink={0}
          gap={"size-200"}
        >
          <View
            position={"relative"}
            width={"5%"}
            left={"8px"}
            top={"5px"}
          >
            {t("notify", { ns: "layout_components" })}:
          </View>
          <View
            width={"92%"}
            marginStart={"17px"}
            UNSAFE_className={styles.label_wrapper}
          >
            <ContactsFilterMultipleTag
              createParams={createParams}
              setContactsSelected={newEmailFields.notify || notifyUsers}
              fieldName={"notify"}
              params={{
                onlyIds: false,
                showOnlyUc: false,
                onlyUser: 1,
                conjuctionDistrGroups: 0,
                ORDER: "",
              }}
            />
          </View>
        </Flex>
        {fromEmailAddress.length > 2 && (
          <Flex
            direction={"row"}
            top={"5px"}
            alignSelf={"stretch"}
            position={"relative"}
            flexShrink={0}
            gap={"size-200"}
          >
            <View
              position={"relative"}
              width={"5%"}
              left={"8px"}
              top={"5px"}
            >
              {t("from", { ns: "layout_components" })}:
            </View>
          <View
              width={"92%"}
              marginStart={"17px"}
              position={"relative"}
              UNSAFE_className={styles.label_wrapper}
            >
              <DropDownList
                value={newEmailFields.fromValue}
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  zIndex: 0,
                  border: "none",
                  marginLeft: "0",
                }}
                data={fromEmailAddress}
                textField="name"
                dataItemKey="id"
                onChange={(event: DropDownListChangeEvent) => {
                  setMailBox(event);
                }}
              />
            </View>
          </Flex>
        )}
      </>
    );
  }

  function renderDesktopEmailPrimaryRow(): React.ReactElement {
    return (
        <Flex
          direction={"row"}
        top={"5px"}
        alignSelf={"stretch"}
        position={"relative"}
        flexShrink={0}
        gap={"size-200"}
      >
        <View
          position={"relative"}
          width={"5%"}
          left={"8px"}
          top={"5px"}
        >
          {t("to", { ns: "layout_components" })}:
        </View>
        <View
          width={"92%"}
          marginStart={"17px"}
          UNSAFE_style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            minWidth: 0,
          }}
        >
          <View
            width={"42%"}
            UNSAFE_className={styles.label_wrapper}
          >
            <ContactsContactMultipleTag
              createParams={createParams}
              fieldName={"to"}
              specificType="email"
              setContactsSelected={newEmailFields.to}
            />
          </View>

          {!showCcNotify ? (
            <View width={"58%"} UNSAFE_className={styles.label_wrapper}>
              <button
                type="button"
                className={styles.advanced_fields_trigger}
                onClick={() => setShowCcNotify(true)}
              >
                {fromEmailAddress.length > 2
                  ? t("cc_notify_from", {
                      ns: "layout_components",
                      defaultValue: "Cc, notify, from:",
                    })
                  : t("cc_notify", {
                      ns: "layout_components",
                      defaultValue: "Cc, notify:",
                    })}
              </button>
            </View>
          ) : (
            <View
              width={"58%"}
              UNSAFE_style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: 0,
              }}
            >
              <View
                position={"relative"}
                width={"10%"}
                top={"5px"}
                UNSAFE_style={{ minWidth: "24px" }}
              >
                {t("cc", { ns: "layout_components" })}:
              </View>
              <View
                width={"92%"}
                UNSAFE_className={styles.label_wrapper}
              >
                <ContactsContactMultipleTag
                  createParams={createParams}
                  fieldName={"cc"}
                  specificType="email"
                  setContactsSelected={newEmailFields.cc}
                />
              </View>
            </View>
          )}
        </View>
      </Flex>
    );
  }

  function renderDesktopNonEmailAdvancedFields(): React.ReactElement {
    if (!showCcNotify) {
      return (
        <Flex
          direction={"row"}
          top={"5px"}
          alignSelf={"stretch"}
          position={"relative"}
          flexShrink={0}
          gap={"size-200"}
        >
          <View
            position={"relative"}
            width={"5%"}
            left={"8px"}
            top={"5px"}
          />
          <View width={"92%"} marginStart={"17px"}>
            <View width={"100%"} UNSAFE_className={styles.label_wrapper}>
              <button
                type="button"
                className={styles.advanced_fields_trigger}
                onClick={() => setShowCcNotify(true)}
              >
                {fromEmailAddress.length > 2
                  ? t("notify_from", {
                      ns: "layout_components",
                      defaultValue: "Notify, from:",
                    })
                  : t("notify_only", {
                      ns: "layout_components",
                      defaultValue: "Notify:",
                    })}
              </button>
            </View>
          </View>
        </Flex>
      );
    }

    return (
      <>
        <Flex
          direction={"row"}
          top={"5px"}
          alignSelf={"stretch"}
          position={"relative"}
          flexShrink={0}
          gap={"size-200"}
        >
          <View
            position={"relative"}
            width={"5%"}
            left={"8px"}
            top={"5px"}
          >
            {t("notify", { ns: "layout_components" })}:
          </View>
          <View
            width={"92%"}
            marginStart={"17px"}
            UNSAFE_className={styles.label_wrapper}
          >
            <ContactsFilterMultipleTag
              createParams={createParams}
              setContactsSelected={newEmailFields.notify || notifyUsers}
              fieldName={"notify"}
              params={{
                onlyIds: false,
                showOnlyUc: false,
                onlyUser: 1,
                conjuctionDistrGroups: 0,
                ORDER: "",
              }}
            />
          </View>
        </Flex>
        {fromEmailAddress.length > 2 && (
          <Flex
            direction={"row"}
            top={"5px"}
            alignSelf={"stretch"}
            position={"relative"}
            flexShrink={0}
            gap={"size-200"}
          >
            <View
              position={"relative"}
              width={"5%"}
              left={"8px"}
              top={"5px"}
            >
              {t("from", { ns: "layout_components" })}:
            </View>
          <View
              width={"92%"}
              marginStart={"17px"}
              position={"relative"}
              UNSAFE_className={styles.label_wrapper}
            >
              <DropDownList
                value={newEmailFields.fromValue}
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  zIndex: 0,
                  border: "none",
                  marginLeft: "0",
                }}
                data={fromEmailAddress}
                textField="name"
                dataItemKey="id"
                onChange={(event: DropDownListChangeEvent) => {
                  setMailBox(event);
                }}
              />
            </View>
          </Flex>
        )}
      </>
    );
  }

  const desktopEditorOffset =
      String(newEmailFields.messageType) === "7"
        ? showCcNotify
          ? 2
          : 2
        : showCcNotify
          ? String(newEmailFields.messageType) === "12"
            ? 2
            : 2
          : 2;

  const desktopEditorHeight = (() => {
      const messageType = String(newEmailFields.messageType);
      let editorHeight = 330;

    if (messageType === "7") {
      editorHeight = showCcNotify ? 290 : 360;
    } else if (messageType === "12") {
      editorHeight = showCcNotify ? 300 : 340;
    } else if (messageType === "6") {
      editorHeight = showCcNotify ? 260 : 300;
    } else if (messageType === "1") {
      editorHeight = showCcNotify ? 280 : 320;
    } else {
      editorHeight = showCcNotify ? 290 : 330;
    }

    if (fileList.length > 0) {
      editorHeight = Math.max(220, editorHeight - 36);
    }

    return editorHeight;
  })();

  const desktopWindowHeight = (() => {
    const messageType = String(newEmailFields.messageType);
    let baseHeight = 740;

    if (messageType === "7") {
      baseHeight = showCcNotify ? 820 : 740;
    } else if (messageType === "12") {
      baseHeight = showCcNotify ? 800 : 760;
    } else if (messageType === "6") {
      baseHeight = showCcNotify ? 840 : 780;
    } else if (messageType === "1") {
      baseHeight = showCcNotify ? 760 : 720;
    } else {
      baseHeight = showCcNotify ? 780 : 740;
    }

    if (fileList.length > 0) {
      baseHeight += 56;
    }

    return Math.min(window.innerHeight - 32, baseHeight);
  })();

  function newMessageDialogheader(): React.ReactElement {
    if (isMobile) {
      return (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"start"}
        >
          <Flex
            direction={"row"}
            UNSAFE_className={styles.message_heading}
            gap={"10px"}
          >
            {t("new_message", { ns: "layout_components" })}{" "}
            <NewMessageIcon size={18} />
          </Flex>
        </Flex>
      );
    } else
      return (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          margin={"10px"}
        >
          <Flex
            direction={"row"}
            UNSAFE_className={styles.message_heading}
            gap={"10px"}
          >
            {t("new_message", { ns: "layout_components" })}{" "}
            <NewMessageIcon size={18} />
          </Flex>
          <Flex
            direction={"row"}
            UNSAFE_className={styles.label_message_type}
            gap={"10px"}
            alignContent={"center"}
            alignItems={"center"}
          >
            {t("message_type", { ns: "layout_components" })}
            <DropDownList
              value={newEmailFields.messageTypeValue}
              style={{ width: "200px", backgroundColor: "white" }}
              data={messageTypeOptions}
              textField="text"
              dataItemKey="id"
              onChange={(event: DropDownListChangeEvent) => {
                setShowCcNotify(false);
                setNewEmailFields({
                  ...newEmailFields,
                  messageType: event.value.type,
                  messageTypeValue: event.value,
                  cc: [],
                  to: [],
                });
              }}
            />
          </Flex>
        </Flex>
      );
  }

  function minimizedDialogheader(): React.ReactElement {
    return (
      <Flex
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Flex
          direction={"row"}
          UNSAFE_className={styles.message_heading_small}
          gap={"10px"}
        >
          {newEmailFields.subject
            ? truncateText(newEmailFields.subject, 15)
            : t("new_message", { ns: "layout_components" })}{" "}
          <NewMessageIcon size={18} />
        </Flex>
      </Flex>
    );
  }

  function newMessageDialogContent(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={"center"} marginTop={300}>
            <Loader size="medium" type={"pulsing"} />
          </Flex>
        );
      } else
        return (
          <Flex
            direction={"column"}
            UNSAFE_className={styles.message_modal}
            UNSAFE_style={{
              height: "calc(100dvh - 150px)",
            }}
          >
            <Flex
              direction={"column"}
              width={"100%"}
              height={"100%"}
              gap={"4px"}
              flex={1}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.message_options}
              UNSAFE_style={{
                display: "grid",
                gridTemplateRows: "auto auto auto auto auto auto auto 1fr", // Form fields + editor (1fr)
              }}
            >
              {/* Message Type Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "1" }}
                width={"calc(100vw - 32px)"}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"18%"}
                >
                  {t("type", {
                    ns: "layout_components",
                    defaultValue: "Type",
                  })} :
                </View>
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"82%"}
                >
                  <DropDownList
                    value={newEmailFields.messageTypeValue}
                    style={{ width: "100%", backgroundColor: "white" }}
                    data={messageTypeOptions}
                    textField="text"
                    dataItemKey="id"
                    onChange={(event: DropDownListChangeEvent) => {
                      setShowCcNotify(false);
                      setNewEmailFields({
                        ...newEmailFields,
                        messageType: event.value.type,
                        messageTypeValue: event.value,
                        cc: [],
                        to: [],
                      });
                    }}
                  />
                </View>
              </Flex>

              {/* Context Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "2", overflow: "visible", zIndex: 2 }}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"18%"}
                >
                  {t("context", { ns: "layout_components" })} :
                </View>
                <View
                  UNSAFE_className={`${styles.label_wrapper_mobile} ${styles.context_selection_host_mobile}`}
                  width={"82%"}
                >
                  <ContextSelection
                    key={`ctx-${String(selectedItemId ?? "none")}`}
                    value={displayContextSelection}
                    onSelectionChange={setMessageContextSelection}
                    folderStatusMap={folderStatusMapping}
                    projectStatusMap={projectStatusMapping}
                    activityStatusMap={activityStatusMapping}
                    isReadOnly={false}
                    showBorders={false}
                    allowClearSelection={true}
                    allowAdd={false}
                    explicitContextId={contextSelectionDefaultValue}
                    onExplicitContextResolveError={onContextPathResolveError}
                    aria-label={t("context", { ns: "layout_components" })}
                  />
                </View>
              </Flex>

              {/* Subject Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "3" }}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"18%"}
                >
                  {t("subject", { ns: "layout_components" })} :
                </View>
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"82%"}
                >
                  <TextField
                    id="txtSubject"
                    ref={txtSubjectRef}
                    defaultValue={newEmailFields.subject}
                    value={newEmailFields.subject}
                    width={"100%"}
                    onChange={(e) =>
                      setNewEmailFields({ ...newEmailFields, subject: e })
                    }
                    UNSAFE_style={{
                      backgroundColor: "transparent !important",
                      padding: "5px",
                      color: "black",
                    }}
                  />
                </View>
              </Flex>

              {/* Conditional Fields Container */}
              <Flex direction={"column"} UNSAFE_style={{ gridRow: "4" }}>
                {String(newEmailFields.messageType) === "7" && (
                  <>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"36px"}
                      marginBottom={"4px"}
                    >
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"18%"}
                      >
                        {t("to", { ns: "layout_components" })} :
                      </View>
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"82%"}
                      >
                        <ContactsContactMultipleTag
                          createParams={createParams}
                          fieldName={"to"}
                          specificType="email"
                          setContactsSelected={newEmailFields.to}
                        />
                      </View>
                    </Flex>

                    {/* CC and Notify Toggle Label */}
                    {!showCcNotify && (
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        position={"relative"}
                      >
                        <div
                          onClick={() => setShowCcNotify(true)}
                          style={{ width: "100%" }}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"100%"}
                          >
                            {t("cc", { ns: "layout_components" })},
                            {t("notify", { ns: "layout_components" })}
                          </View>
                        </div>
                      </Flex>
                    )}

                    {/* CC and Notify Fields (Collapsible) */}
                    {showCcNotify && (
                      <>
                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"36px"}
                          marginBottom={"4px"}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"18%"}
                          >
                            {t("cc", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"82%"}
                          >
                            <ContactsContactMultipleTag
                              createParams={createParams}
                              fieldName={"cc"}
                              specificType="email"
                              setContactsSelected={newEmailFields.cc}
                            />
                          </View>
                        </Flex>

                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"36px"}
                          marginBottom={"4px"}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"18%"}
                          >
                            {t("notify", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"82%"}
                          >
                            <View marginTop={"5px"}>
                              <ContactsFilterMultipleTag
                                createParams={createParams}
                                setContactsSelected={newEmailFields.notify}
                                fieldName={"notify"}
                                params={{
                                  onlyIds: false,
                                  showOnlyUc: false,
                                  onlyUser: 1,
                                  conjuctionDistrGroups: 0,
                                  ORDER: "",
                                }}
                              />
                            </View>
                          </View>
                        </Flex>
                      </>
                    )}
                  </>
                )}

                {newEmailFields.messageType === "12" && (
                  <Flex
                    direction={"row"}
                    alignSelf={"stretch"}
                    position={"relative"}
                    height={"36px"}
                    marginBottom={"4px"}
                  >
                    <View
                      UNSAFE_className={styles.label_wrapper_mobile}
                      width={"18%"}
                    >
                      {t("meeting", { ns: "layout_components" })} :
                    </View>
                    <View
                      UNSAFE_className={styles.label_wrapper_mobile}
                      width={"82%"}
                    >
                      <ContactsFilterMultipleTag
                        createParams={createParams}
                        fieldName={"participants"}
                        setContactsSelected={newEmailFields.participants}
                        params={{
                          onlyIds: false,
                          showOnlyUc: false,
                          conjuctionDistrGroups: 0,
                          ORDER: "",
                        }}
                      />
                    </View>
                  </Flex>
                )}

                {newEmailFields.messageType === "6" && (
                  <>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"36px"}
                      marginBottom={"4px"}
                    >
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"18%"}
                      >
                        {t("phone", { ns: "layout_components" })} :
                      </View>
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"82%"}
                      >
                        <ContactsContactMultipleTag
                          createParams={createParams}
                          fieldName={"to"}
                          specificType="phone"
                          setContactsSelected={newEmailFields.to}
                        />
                      </View>
                    </Flex>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                    >
                      <Flex width={"100%"}>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="131072"
                            checked={newEmailFields.phoneStatus === "131072"}
                            label={t("incoming", { ns: "layout_components" })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="262144"
                            checked={newEmailFields.phoneStatus === "262144"}
                            label={t("outgoing", { ns: "layout_components" })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="524288"
                            checked={newEmailFields.phoneStatus === "524288"}
                            label={t("unsuccessful", {
                              ns: "layout_components",
                            })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>

              {String(newEmailFields.messageType) !== "7" && (
                <>
                  {!showCcNotify ? (
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"36px"}
                      marginBottom={"4px"}
                      UNSAFE_style={{ gridRow: "5" }}
                    >
                      <div
                        onClick={() => setShowCcNotify(true)}
                        style={{ width: "100%" }}
                      >
                        <View
                          UNSAFE_className={styles.label_wrapper_mobile}
                          width={"100%"}
                        >
                          {fromEmailAddress.length > 2
                            ? t("notify_from", {
                                ns: "layout_components",
                                defaultValue: "Notify, from:",
                              })
                            : t("notify_only", {
                                ns: "layout_components",
                                defaultValue: "Notify:",
                              })}
                        </View>
                      </div>
                    </Flex>
                  ) : (
                    <>
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        position={"relative"}
                        height={"36px"}
                        marginBottom={"4px"}
                        UNSAFE_style={{ gridRow: "5" }}
                      >
                        <View
                          UNSAFE_className={styles.label_wrapper_mobile}
                          width={"18%"}
                        >
                          {t("notify", { ns: "layout_components" })} :
                        </View>
                        <View
                          UNSAFE_className={styles.label_wrapper_mobile}
                          width={"82%"}
                        >
                          <View marginTop={"5px"}>
                            <ContactsFilterMultipleTag
                              createParams={createParams}
                              setContactsSelected={newEmailFields.notify}
                              fieldName={"notify"}
                              params={{
                                onlyIds: false,
                                showOnlyUc: false,
                                onlyUser: 1,
                                conjuctionDistrGroups: 0,
                                ORDER: "",
                              }}
                            />
                          </View>
                        </View>
                      </Flex>
                      {fromEmailAddress.length > 2 && (
                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"36px"}
                          marginBottom={"4px"}
                          UNSAFE_style={{ gridRow: "6" }}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"18%"}
                          >
                            {t("from", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"82%"}
                          >
                            <View marginTop={"5px"}>
                              <DropDownList
                                value={newEmailFields.fromValue}
                                style={{
                                  width: "100%",
                                  backgroundColor: "white",
                                  zIndex: 0,
                                  border: "none",
                                  marginLeft: "0",
                                }}
                                data={fromEmailAddress}
                                textField="name"
                                dataItemKey="id"
                                onChange={(event: DropDownListChangeEvent) => {
                                  setMailBox(event);
                                }}
                              />
                            </View>
                          </View>
                        </Flex>
                      )}
                    </>
                  )}
                </>
              )}

              {/* File List (Conditional) */}
              {fileList.length > 0 && (
                <Flex
                  direction={"column"}
                  position={"relative"}
                  width={"100%"}
                  UNSAFE_style={{ gridRow: "7" }}
                >
                  <Flex
                    wrap
                    direction="row"
                    gap="size-100"
                    alignItems="center"
                    width="100%"
                    UNSAFE_className={styles.attachment_list_container}
                    UNSAFE_style={{
                      backgroundColor: "#f5f7f8",
                      borderRadius: "8px",
                      padding: "8px",
                      zIndex: 0,
                    }}
                  >
                    {fileList.map((item: any, index: number) => (
                      <View
                        key={item.id}
                        width={"30%"}
                        position={"relative"}
                        height={"20px"}
                        UNSAFE_className={styles.icon_cancel_parent}
                        id={`${item.id}_${index}`}
                      >
                        <View
                          position={"relative"}
                          width={"24px"}
                          height={"24px"}
                          UNSAFE_className={styles.icon_cancel}
                        >
                          <div onClick={() => deleteFile(item.id)}>
                            <CancelIcon size={18} />
                          </div>
                        </View>
                        <View
                          position={"relative"}
                          left={"5px"}
                          width={"70%"}
                          UNSAFE_className={styles.label_attachment}
                        >
                          {truncateText(item.fileName, 10)}
                        </View>
                        <View
                          position={"absolute"}
                          left={"8px"}
                          width={"28px"}
                          height={"28px"}
                          UNSAFE_className={styles.attachment_icon}
                        >
                          <AttachmentIcon size={18} />
                        </View>
                      </View>
                    ))}
                  </Flex>
                </Flex>
              )}

              {/* TinyMCE Editor - Takes remaining space */}
              <Flex
                direction={"column"}
                position={"relative"}
                width={"100%"}
                height={"100%"}
                alignItems={"start"}
                justifyContent={"start"}
                zIndex={0}
                flex={1}
                minHeight={"220px"}
                gridRow={"8"}
                marginTop={"8px"}
                marginX={"0"}
              >
                <TinyMCEEditor
                  key={"tiny_editor_mobile"}
                  initialValue={newEmailFields.emailContent}
                  onChange={(e: any) => {
                    setNewEmailFields({ ...newEmailFields, emailContent: e });
                  }}
                  onSubmitShortcut={() => {
                    if (!showLoader) {
                      handleSendEmail();
                    }
                  }}
                  onEscapeShortcut={() => closeDialog()}
                  onCloseShortcut={() => closeDialog()}
                  autoFocus={false}
                  height={"100%"}
                />
              </Flex>
            </Flex>
          </Flex>
        );
    }
    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={"center"} marginTop={300}>
          <Loader size="medium" type={"pulsing"} />
        </Flex>
      );
    } else {
      return (
        <div
          {...getRootProps()}
          className={isDragActive ? styles.drag_active : ""}
          style={{
            height: "auto",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <input {...getInputProps()} />

          {/* Overlay that appears only when dragging */}
          {isDragActive && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
                backgroundColor: "rgba(38, 128, 235, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Text UNSAFE_style={{ fontWeight: "bold", fontSize: "18px" }}>
                {t("drop_files_here", { ns: "layout_components" })}
              </Text>
            </div>
          )}
          <View
            UNSAFE_className={styles.message_modal}
            UNSAFE_style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <View
              width={"100%"}
              flexGrow={1}
              flexShrink={1}
              UNSAFE_style={{
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Flex
                direction={"column"}
                width={"100%"}
                alignItems={"start"}
                justifyContent={"start"}
                UNSAFE_className={styles.message_options}
                UNSAFE_style={{ minHeight: 0 }}
              >
              <Flex
                direction={"row"}
                top={"2px"}
                alignSelf={"stretch"}
                position={"relative"}
                flexShrink={0}
                gap={"size-200"}
                UNSAFE_style={{ overflow: "visible", zIndex: 2 }}
              >
                <View position={"relative"} width={"5%"} left={"8px"} top={"2px"}>
                  {t("context", { ns: "layout_components" })}:
                </View>
                <View
                  width={"92%"}
                  marginStart={"17px"}
                  UNSAFE_className={`${styles.label_wrapper} ${styles.context_selection_host}`}
                >
                  <ContextSelection
                    key={`ctx-${String(selectedItemId ?? "none")}`}
                    value={displayContextSelection}
                    onSelectionChange={setMessageContextSelection}
                    folderStatusMap={folderStatusMapping}
                    projectStatusMap={projectStatusMapping}
                    activityStatusMap={activityStatusMapping}
                    isReadOnly={false}
                    showBorders={false}
                    allowClearSelection={true}
                    allowAdd={false}
                    allowTransparentBG={true}
                    explicitContextId={contextSelectionDefaultValue}
                    onExplicitContextResolveError={onContextPathResolveError}
                    aria-label={t("context", { ns: "layout_components" })}
                  />
                </View>
              </Flex>
              <Flex
                direction={"row"}
                top={"2px"}
                alignSelf={"stretch"}
                position={"relative"}
                flexShrink={0}
                gap={"size-200"}
              >
                <View
                  position={"relative"}
                  width={"5%"}
                  left={"8px"}
                  alignSelf={"center"}
                >
                  {t("subject", { ns: "layout_components" })}:
                </View>
                <View
                  width={"92%"}
                  marginStart={"17px"}
                  UNSAFE_className={styles.label_wrapper}
                >
                  <TextField
                    id="txtSubject"
                    ref={txtSubjectRef}
                    defaultValue={newEmailFields.subject}
                    value={newEmailFields.subject}
                    width={"100%"}
                    height={"25px"}
                    onChange={(e) =>
                      setNewEmailFields({ ...newEmailFields, subject: e })
                    }
                    UNSAFE_style={{
                      border: "none !important",
                      boxShadow: "none",
                      backgroundColor: "transparent !important",
                      padding: "5px 0px",
                      color: "black",
                    }}
                    UNSAFE_className="borderless_textfield"
                  />
                </View>
              </Flex>
              {String(newEmailFields.messageType) === "7" &&
                renderDesktopEmailPrimaryRow()}
              {String(newEmailFields.messageType) === "7" &&
                showCcNotify &&
                renderDesktopEmailOptionalFields()}
                {String(newEmailFields.messageType) === "12" && (
                  <Flex
                    direction={"row"}
                    top={"5px"}
                    alignSelf={"stretch"}
                    position={"relative"}
                    flexShrink={0}
                    gap={"size-200"}
                  >
                    <View
                      position={"relative"}
                      width={"5%"}
                      left={"8px"}
                      top={"5px"}
                    >
                      {t("participants", {
                        ns: "layout_components",
                        defaultValue: "Participants",
                      })}
                      :
                    </View>
                    <View
                      width={"92%"}
                      marginStart={"17px"}
                      UNSAFE_className={styles.label_wrapper}
                    >
                      <ContactsFilterMultipleTag
                        createParams={createParams}
                        fieldName={"participants"}
                        setContactsSelected={newEmailFields.participants}
                        params={{
                          onlyIds: false,
                          //organizationId: 130946101,
                          showOnlyUc: false,
                          conjuctionDistrGroups: 0,
                          ORDER: "",
                        }}
                      />
                    </View>
                  </Flex>
                )}

                {String(newEmailFields.messageType) === "6" && (
                <Flex
                  direction={"row"}
                  top={"5px"}
                  alignSelf={"stretch"}
                  position={"relative"}
                  flexShrink={0}
                  gap={"size-200"}
                >
                  <View
                    position={"relative"}
                    width={"5%"}
                    left={"8px"}
                    top={"5px"}
                  >
                    {t("phone", { ns: "layout_components" })}:
                  </View>
                  <View
                    width={"92%"}
                    marginStart={"17px"}
                    UNSAFE_style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "16px",
                      minWidth: 0,
                    }}
                  >
                    <View
                      width={"42%"}
                      UNSAFE_className={styles.label_wrapper}
                    >
                      <ContactsContactMultipleTag
                        createParams={createParams}
                        fieldName={"to"}
                        specificType="phone"
                        setContactsSelected={newEmailFields.to}
                      />
                    </View>

                    <Flex
                      direction={"row"}
                      gap={"20px"}
                      width={"58%"}
                      alignItems={"center"}
                      UNSAFE_className={styles.label_wrapper}
                    >
                      <RadioButton
                        name="phoneStatus"
                        value="131072"
                        checked={newEmailFields.phoneStatus === "131072"}
                        label={t("incoming", { ns: "layout_components" })}
                        onChange={(event) => {
                          setNewEmailFields({
                            ...newEmailFields,
                            phoneStatus: event.value,
                          });
                        }}
                      />

                      <RadioButton
                        name="phoneStatus"
                        value="262144"
                        checked={newEmailFields.phoneStatus === "262144"}
                        label={t("outgoing", { ns: "layout_components" })}
                        onChange={(event) => {
                          setNewEmailFields({
                            ...newEmailFields,
                            phoneStatus: event.value,
                          });
                        }}
                      />

                      <RadioButton
                        name="phoneStatus"
                        value="524288"
                        checked={newEmailFields.phoneStatus === "524288"}
                        label={t("unsuccessful", { ns: "layout_components" })}
                        onChange={(event) => {
                          setNewEmailFields({
                            ...newEmailFields,
                            phoneStatus: event.value,
                          });
                        }}
                      />
                    </Flex>
                  </View>
                </Flex>
              )}
              {String(newEmailFields.messageType) !== "7" &&
                renderDesktopNonEmailAdvancedFields()}
            </Flex>
            <Flex
              direction={"column"}
              position={"relative"}
              marginTop={`${desktopEditorOffset}px`}
            >
              {fileList.length > 0 && (
                <Flex
                  wrap
                  direction="row"
                  gap="size-100"
                  alignItems="center"
                  width="98%"
                  marginStart="11px"
                  marginTop={"10px"}
                  UNSAFE_className={styles.attachment_list_container}
                  UNSAFE_style={{
                    backgroundColor: "#f5f7f8",
                    padding: "10px",
                    borderRadius: "8px",
                    zIndex: 0,
                    maxHeight: "168px",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {fileList.map((item: any, index: number) => (
                    <View
                      key={item.id}
                      width={"48%"}
                      position={"relative"}
                      height={"40px"}
                      UNSAFE_className={styles.icon_cancel_parent}
                      UNSAFE_style={{ display: "flex", alignItems: "center" }}
                      id={`${item.id}_${index}`}
                    >
                      {/* Attachment icon – absolute left */}
                      <View
                        position={"absolute"}
                        top={"6px"}
                        left={"8px"}
                        width={"28px"}
                        height={"28px"}
                        UNSAFE_className={styles.attachment_icon}
                      >
                        <AttachmentIcon size={18} />
                      </View>
                      {/* Filename button – fills middle */}
                      <button
                        disabled={isProcessingAttachment}
                        style={{
                          flex: 1,
                          border: "none",
                          background: "transparent",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                          textAlign: "left",
                          paddingLeft: "40px",
                          paddingRight: "28px",
                          fontWeight: "bold",
                          fontSize: "14px",
                          cursor: "pointer",
                          height: "40px",
                          opacity:
                            processingAttachmentId === String(item.id)
                              ? 0.6
                              : 1,
                        }}
                        onClick={async () => {
                          setProcessingAttachmentId(String(item.id));
                          await showAttachment(item.base64Files, item.fileName);
                        }}
                      >
                        {processingAttachmentId === String(item.id) ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <ProgressCircle
                              aria-label="Loading"
                              isIndeterminate
                              size="S"
                            />
                            {truncateText(item.fileName, 15)}
                          </span>
                        ) : (
                          truncateText(item.fileName, 20)
                        )}
                      </button>
                      {/* Cancel icon – absolute right */}
                      <View
                        position={"absolute"}
                        top={"8px"}
                        UNSAFE_style={{ right: "8px" }}
                        width={"24px"}
                        height={"24px"}
                      >
                        <div onClick={() => deleteFile(item.id)}>
                          <CancelIcon size={18} />
                        </div>
                      </View>
                    </View>
                  ))}
                </Flex>
              )}
            </Flex>

              <Flex
                direction={"column"}
                position={"relative"}
                marginTop={"4px"}
                marginBottom={"8px"}
                width={"calc(100% - 16px)"}
                height={"auto"}
              alignItems={"start"}
              justifyContent={"start"}
              zIndex={0}
              flexShrink={0}
              marginX={"8px"}
              UNSAFE_style={{ overflow: "hidden" }}
            >
              <TinyMCEEditor
                initialValue={newEmailFields.emailContent}
                onChange={(e: any) => {
                  setNewEmailFields({ ...newEmailFields, emailContent: e });
                }}
                onSubmitShortcut={() => {
                  if (!showLoader) {
                    handleSendEmail();
                  }
                }}
                onEscapeShortcut={() => closeDialog()}
                onCloseShortcut={() => closeDialog()}
                autoFocus={false}
                height={desktopEditorHeight}
              />
            </Flex>
            </View>
          </View>
        </div>
      );
    }
  }

  function newMessageDialogFooter(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={"center"}>
            <Loader size="medium" type={"pulsing"} />
          </Flex>
        );
      } else
        return (
          <Flex
            gap={"10px"}
            direction={"column"}
            alignItems={"start"}
            UNSAFE_className={styles.email_action_bar}
          >
            <Flex gap={"8px"} alignItems={"center"}>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "10px" }}>
                  <TemplateIcon size={18} />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("use_template", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "10px", color: "#1976d2" }}>
                  <i className="bi bi-gear" />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("advanced", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <FileTrigger
                allowsMultiple
                onSelect={async (e: any) => {
                  handleFilesAsync(e);
                }}
              >
                <ActionButton UNSAFE_className={styles.icon_action_button}>
                  <Content UNSAFE_style={{ marginLeft: "10px" }}>
                    <AttachmentIcon size={18} />
                  </Content>
                  <Text UNSAFE_className={styles.btn_attachment}>
                    {t("add_attachment", { ns: "layout_components" })}
                  </Text>
                </ActionButton>
              </FileTrigger>
            </Flex>
            <Flex gap={"8px"} alignItems={"center"}>
              {/* <button className="action-button draft-button">Save as draft and close</button> */}
              <ActionButton
                onPress={(e) => handleSaveDraft()}
                UNSAFE_className={`${styles.action_button} ${styles.draft_button}`}
              >
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("save_draft_and_close", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              {/* <button className="action-button send-button">Send</button> */}
              <ActionButton
                onPress={(e) =>
                  newEmailFields.messageType === "1" ||
                  newEmailFields.messageType === "12" ||
                  newEmailFields.messageType === "6"
                    ? handleSendEmail()
                    : handleSendEmail()
                }
                UNSAFE_className={`${styles.action_button} ${styles.send_button}`}
              >
                <Text UNSAFE_className={styles.btn_send}>
                  {t("send", { ns: "layout_components" })}
                </Text>
              </ActionButton>
            </Flex>
          </Flex>
        );
    }
    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={"center"}>
          <Loader size="medium" type={"pulsing"} />
        </Flex>
      );
    } else
      return (
        <WindowActionsBar layout={"stretched"}>
          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={"10px"}
            width={"100%"}
            UNSAFE_className={styles.email_action_bar}
          >
            <Flex gap={"8px"} alignItems={"center"}>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "25px" }}>
                  <TemplateIcon size={18} />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("use_template", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "25px", color: "#1976d2" }}>
                  <i className="bi bi-gear" />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("advanced", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <FileTrigger
                allowsMultiple
                onSelect={async (e: any) => {
                  handleFilesAsync(e);
                }}
              >
                <ActionButton UNSAFE_className={styles.icon_action_button}>
                  <Content UNSAFE_style={{ marginLeft: "25px" }}>
                    <AttachmentIcon size={18} />
                  </Content>
                  <Text UNSAFE_className={styles.btn_attachment}>
                    {t("add_attachment", { ns: "layout_components" })}
                  </Text>
                </ActionButton>
              </FileTrigger>
            </Flex>
            <Flex gap={"8px"} alignItems={"center"}>
              <ActionButton
                onPress={(e) => handleSaveDraft()}
                UNSAFE_className={`${styles.action_button} ${styles.draft_button}`}
              >
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("save_draft_and_close", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <ActionButton
                onPress={(e) =>
                  newEmailFields.messageType === "1" ||
                  newEmailFields.messageType === "12" ||
                  newEmailFields.messageType === "6"
                    ? handleSendEmail()
                    : handleSendEmail()
                }
                UNSAFE_className={`${styles.action_button} ${styles.send_button}`}
              >
                <Text UNSAFE_className={styles.btn_send}>
                  {t("send", { ns: "layout_components" })}
                </Text>
              </ActionButton>
            </Flex>
          </Flex>
        </WindowActionsBar>
      );
  }

  async function showAttachment(fileSource: any, fileName?: string) {
    try {
      /** show loading indicator on attachemnt link when click */
      setIsProcessingAttachment(true);
      //console.log('fileSource', fileSource);

      // Get file extension from fileName if available
      const getFileExtension = (name: string) => {
        return name ? name.split(".").pop()?.toLowerCase() || "" : "";
      };

      const extension = fileName ? getFileExtension(fileName) : "";

      // Handle different file types appropriately
      if (extension === "pdf") {
        // Only open PDF files with PDF viewer
        try {
          await openPdfContentInNewWindow(fileSource);
        } catch (error) {
          console.error("Error opening PDF:", error);
          ToastQueue.negative("Error opening PDF file", { timeout: 3000 });
        }
      } else if (
        extension &&
        ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(extension)
      ) {
        // Handle image files - create image preview
        try {
          const imageUrl =
            typeof fileSource === "string"
              ? fileSource
              : URL.createObjectURL(new Blob([fileSource]));
          const newWindow = window.open("", "_blank");
          if (newWindow) {
            newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Image Preview - ${fileName || "Image"}</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f0f0; }
                img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              </style>
            </head>
            <body>
              <img src="${imageUrl}" alt="${fileName || "Image"}" />
            </body>
          </html>
        `);
          }
        } catch (error) {
          console.error("Error opening image:", error);
          ToastQueue.negative("Error opening image file", { timeout: 3000 });
        }
      } else if (extension && ["txt", "json", "csv"].includes(extension)) {
        // Handle text files - show content in new window
        try {
          let textContent = "";
          if (typeof fileSource === "string") {
            // If it's base64, decode it
            if (fileSource.includes(",")) {
              textContent = atob(fileSource.split(",")[1]);
            } else {
              textContent = atob(fileSource);
            }
          }

          const newWindow = window.open("", "_blank");
          if (newWindow) {
            newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Text Preview - ${fileName || "Text File"}</title>
              <style>
                body { font-family: monospace; margin: 20px; white-space: pre-wrap; }
              </style>
            </head>
            <body>${textContent}</body>
          </html>
        `);
          }
        } catch (error) {
          console.error("Error opening text file:", error);
          ToastQueue.negative("Error opening text file", { timeout: 3000 });
        }
      } else {
        // For other file types (docx, xlsx, etc.), show info message
        ToastQueue.info(
          `${
            fileName || "File"
          } will be available for download after sending the message`,
          { timeout: 3000 },
        );
      }
    } catch (error) {
      console.error("Error processing attachment:", error);
      ToastQueue.negative("Error processing attachment", { timeout: 3000 });
    } finally {
      setIsProcessingAttachment(false);
      setProcessingAttachmentId(null);
    }
  }

  return (
    <div onKeyUp={handleClose}>
      <WindowComponent
        id={`new-message-window_${windowIndex}`}
        windowIndex={windowIndex}
        visible={true}
        title={newMessageDialogheader()}
        minimizedTitle={minimizedDialogheader()}
        content={newMessageDialogContent()}
        footer={!isMobile ? newMessageDialogFooter() : undefined}
        className={styles.compose_window_shell}
        width={isMobile ? window.innerWidth : Math.min(window.innerWidth - 32, 920)}
        height={isMobile ? window.innerHeight : desktopWindowHeight}
        draggable={!isMobile}
        resizable={!isMobile}
        stickFooterToBottom={false}
        zIndex={1000}
        onClose={() => closeDialog()}
      />
    </div>
  );
}

export default NewMessageDialog;
